/* -------------------------------------------------------------------------- */
/* Dependencies                                */
/* -------------------------------------------------------------------------- */
import i18n from '@config/i18n/config';
import { apiClient } from '@lib/api-client';
import { TMutationConfig } from '@lib/react-query';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

/* -------------------------------------------------------------------------- */
/* Types                          									          */
/* -------------------------------------------------------------------------- */

export interface IContactResponse {
	id: string;
	prenom: string;
	nom: string;
	email: string;
}

interface ICreateContactPayload {
	firstName: string;
	lastName: string;
	email: string;
	locale: {
		language: string;
	};
	phoneNumber?: string;
	organization: string;
}

type TUseCreateContactOptions = {
	mutationConfig?: TMutationConfig<typeof createContact>;
};

/* -------------------------------------------------------------------------- */
/* API                                 									      */
/* -------------------------------------------------------------------------- */
export const createContact = ({
	body,
}: {
	body: ICreateContactPayload;
}): Promise<IContactResponse> => {
	return apiClient.post('/users', body);
};

/* -------------------------------------------------------------------------- */
/* Hook                                    									  */
/* -------------------------------------------------------------------------- */
export const useCreateContact = ({
	mutationConfig,
}: TUseCreateContactOptions = {}) => {
	const { onSuccess, onError, ...restConfig } =
		mutationConfig || {};

	return useMutation({
		mutationFn: createContact,
		onSuccess: (...args) => {
			notifications.show({
				title: i18n.t('global.success'),
				message: i18n.t('global.contactCreatedSuccess'),
				color: 'green',
			});
			onSuccess?.(...args);
		},
		onError: (...args) => {
			const errorMessage = args[0]?.data?.email?.message;
			if (errorMessage == 'ALREADY_USED_EMAIL') {
				notifications.show({
					title: i18n.t('global.error'),
					message: i18n.t('api.ALREADY_USED_EMAIL'),
					color: 'var(--mantine-color-igluRed-9)',
				});
				onError?.(...args);
				return;
			}
			notifications.show({
				title: i18n.t('global.error'),
				message: i18n.t('global.somethingWentWrong'),
				color: 'var(--mantine-color-igluRed-9)',
			});
			onError?.(...args);
		},
		...restConfig,
	});
};
